import React, { useMemo } from 'react';
import { Box, Card, LinearProgress, Stack, Typography } from '@mui/material';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';

interface StreakCardProps {
  current: number;
  longest: number;
  activeToday: boolean;
}

// Rubicon ladder built from primes. Short hops at the start (2,3,5,7,11) keep
// momentum for a fresh user; spacing stretches naturally so 100+ day streaks
// still have something to chase without round-number guilt ("28 / 30 days").
const MILESTONES = [
  2, 3, 5, 7, 11, 13, 17, 19, 23, 29, 31, 41, 53, 67, 89, 113, 151, 199, 271, 367,
];

// How many pills to surface around the current position. Showing every prime
// turns the track into a barcode — keep it to a focused window.
const TRACK_BEFORE = 2;
const TRACK_AFTER = 5;

const findNextMilestone = (streak: number): number | null => {
  for (const m of MILESTONES) {
    if (streak < m) return m;
  }
  return null;
};

const findPrevMilestone = (streak: number): number => {
  let prev = 0;
  for (const m of MILESTONES) {
    if (streak >= m) prev = m;
    else break;
  }
  return prev;
};

const buildVisibleTrack = (streak: number): number[] => {
  // Centre the window so that ~TRACK_BEFORE reached + TRACK_AFTER upcoming are
  // visible. Falls back to head/tail of the list when streak is at either end.
  const nextIdx = MILESTONES.findIndex((m) => streak < m);
  const pivot = nextIdx === -1 ? MILESTONES.length : nextIdx;
  const start = Math.max(0, pivot - TRACK_BEFORE);
  const end = Math.min(MILESTONES.length, pivot + TRACK_AFTER);
  return MILESTONES.slice(start, end);
};

const StreakCard: React.FC<StreakCardProps> = ({
  current,
  longest,
  activeToday,
}) => {
  const next = useMemo(() => findNextMilestone(current), [current]);
  const prev = useMemo(() => findPrevMilestone(current), [current]);

  const flameColor = activeToday ? '#FF7043' : 'rgba(255,112,67,0.35)';

  const segmentLen = next ? next - prev : 0;
  const segmentDone = next ? current - prev : 0;
  const pct = next ? Math.min(100, Math.round((segmentDone / segmentLen) * 100)) : 100;
  const remaining = next ? Math.max(0, next - current) : 0;

  return (
    <Card sx={{ height: '100%' }}>
      <Stack spacing={2.5} sx={{ height: '100%' }}>
        <Typography color={'text.secondary'} fontWeight={700}>
          {'Серія днів'}
        </Typography>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={3}
          alignItems={{ xs: 'flex-start', sm: 'stretch' }}
          sx={{ flex: 1 }}
        >
          <CurrentStreak
            current={current}
            activeToday={activeToday}
            flameColor={flameColor}
          />

          <Box
            sx={(theme) => ({
              borderLeft: {
                xs: 'none',
                sm: `1px solid ${
                  theme.palette.mode === 'dark'
                    ? '#303A48'
                    : theme.palette.primary.tableBorder
                }`,
              },
              borderTop: {
                xs: `1px solid ${
                  theme.palette.mode === 'dark'
                    ? '#303A48'
                    : theme.palette.primary.tableBorder
                }`,
                sm: 'none',
              },
              flex: 1,
              pl: { xs: 0, sm: 3 },
              pt: { xs: 2, sm: 0 },
              width: { xs: '100%', sm: 'auto' },
            })}
          >
            <NextMilestone
              next={next}
              prev={prev}
              current={current}
              pct={pct}
              remaining={remaining}
            />
          </Box>
        </Stack>

        <Stack
          direction={'row'}
          alignItems={'center'}
          spacing={1}
          sx={{
            color: 'text.secondary',
            mt: 'auto',
          }}
        >
          <EmojiEventsOutlinedIcon sx={{ fontSize: 16 }} />
          <Typography variant={'body2'}>
            {'Найдовша серія: '}
            <Box component={'span'} sx={{ color: 'text.primary', fontWeight: 700 }}>
              {`${longest} днів`}
            </Box>
          </Typography>
        </Stack>
      </Stack>
    </Card>
  );
};

const CurrentStreak: React.FC<{
  current: number;
  activeToday: boolean;
  flameColor: string;
}> = ({ current, activeToday, flameColor }) => (
  <Box
    sx={{
      flexShrink: 0,
      minWidth: { sm: 140 },
    }}
  >
    <Stack direction={'row'} alignItems={'center'} spacing={1.5}>
      <Box
        sx={{
          alignItems: 'center',
          color: flameColor,
          display: 'flex',
          // Subtle pulse on the flame when the streak is alive today —
          // makes the difference between "did it" and "didn't yet" obvious.
          ...(activeToday && {
            animation: 'streak-pulse 2s ease-in-out infinite',
            '@keyframes streak-pulse': {
              '0%, 100%': { transform: 'scale(1)' },
              '50%': { transform: 'scale(1.08)' },
            },
          }),
        }}
      >
        <LocalFireDepartmentIcon sx={{ fontSize: 48 }} />
      </Box>
      <Box>
        <Typography
          sx={{
            color: activeToday ? '#FF7043' : 'text.secondary',
            fontSize: 56,
            fontWeight: 800,
            lineHeight: 1,
          }}
        >
          {current}
        </Typography>
        <Typography variant={'caption'} color={'text.secondary'}>
          {current === 1 ? 'день' : 'днів поспіль'}
        </Typography>
      </Box>
    </Stack>
    <Typography
      variant={'body2'}
      sx={{
        color: activeToday ? 'primary.aqua' : 'secondary.main',
        fontWeight: 600,
        mt: 1.5,
      }}
    >
      {activeToday ? '✓ Сьогодні зараховано' : '⚠ Сьогодні ще порожньо'}
    </Typography>
  </Box>
);

const NextMilestone: React.FC<{
  next: number | null;
  prev: number;
  current: number;
  pct: number;
  remaining: number;
}> = ({ next, current, pct, remaining }) => {
  if (!next) {
    return (
      <Stack spacing={1}>
        <Typography variant={'caption'} color={'text.secondary'}>
          {'Наступний рубіж'}
        </Typography>
        <Typography variant={'h4'} sx={{ color: 'primary.main' }}>
          {'Усі рубежі пройдено'}
        </Typography>
        <Typography variant={'body2'} color={'text.secondary'}>
          {'Тепер кожен день — твій особистий рекорд.'}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={1.25}>
      <Stack direction={'row'} justifyContent={'space-between'} alignItems={'baseline'}>
        <Typography variant={'caption'} color={'text.secondary'}>
          {'Наступний рубіж'}
        </Typography>
        <Typography
          variant={'caption'}
          sx={{ color: 'primary.main', fontWeight: 700 }}
        >
          {`Ще ${remaining} ${remaining === 1 ? 'день' : 'днів'}`}
        </Typography>
      </Stack>

      <Stack direction={'row'} alignItems={'baseline'} spacing={1}>
        <Typography variant={'h3'} sx={{ color: 'primary.main' }}>
          {next}
        </Typography>
        <Typography variant={'body2'} color={'text.secondary'}>
          {'днів поспіль'}
        </Typography>
      </Stack>

      <Box>
        <LinearProgress
          value={pct}
          variant={'determinate'}
          sx={{
            bgcolor: 'primary.vocabularyUnlearned',
            borderRadius: 10,
            height: 8,
            '& .MuiLinearProgress-bar': {
              bgcolor: 'primary.main',
              transition: 'transform 500ms ease',
            },
          }}
        />
        <Stack direction={'row'} justifyContent={'space-between'} sx={{ mt: 0.75 }}>
          <Typography variant={'caption'} color={'text.secondary'}>
            {`${current} з ${next}`}
          </Typography>
          <Typography
            variant={'caption'}
            sx={{ color: 'primary.main', fontWeight: 700 }}
          >
            {`${pct}%`}
          </Typography>
        </Stack>
      </Box>

      <MilestoneTrack milestones={buildVisibleTrack(current)} current={current} />
    </Stack>
  );
};

const MilestoneTrack: React.FC<{
  milestones: number[];
  current: number;
}> = ({ milestones, current }) => (
  <Stack direction={'row'} spacing={0.5} sx={{ mt: 0.5 }}>
    {milestones.map((m) => {
      const reached = current >= m;
      return (
        <Box
          key={m}
          sx={(theme) => ({
            alignItems: 'center',
            border: `1px solid ${
              reached
                ? theme.palette.primary.main
                : theme.palette.mode === 'dark'
                ? '#303A48'
                : theme.palette.primary.tableBorder
            }`,
            borderRadius: 1,
            color: reached ? 'primary.main' : 'text.secondary',
            display: 'flex',
            flex: 1,
            fontSize: 11,
            fontWeight: 700,
            height: 22,
            justifyContent: 'center',
            bgcolor: reached
              ? theme.palette.mode === 'dark'
                ? 'rgba(155,124,255,0.15)'
                : 'rgba(59,44,147,0.08)'
              : 'transparent',
          })}
        >
          {m}
        </Box>
      );
    })}
  </Stack>
);

// Re-export the heat colour palette so MiniHeatmap / ActivityHeatmap can keep
// importing from here. (StreakCard no longer renders a heatmap itself.)
export const heatColor = (level: number, dark = false): string => {
  if (dark) {
    const palette = ['#1F2735', '#2E4D35', '#3E7150', '#4E9A6C', '#5BC689'];
    return palette[Math.max(0, Math.min(4, level))];
  }
  const palette = ['#EBEDF0', '#D1E1BA', '#9BC79E', '#5FA56F', '#2F7A4B'];
  return palette[Math.max(0, Math.min(4, level))];
};

export default StreakCard;
